import { AufnahmePunkt, HorizontZeile, HumusAuflage, SubstratZeile } from '../../data-model';

/**
 * Horizont- und Substratfolge sind ZWEI getrennte Tiefenfolgen mit eigenen
 * Grenzen, die nicht zusammenfallen muessen (Grube 001 Torgelow: Horizonte bei
 * 18/55/160/300 cm, Substrate bei 18/55/220/230/300 cm).
 *
 * Fuer das Bild werden alle Grenzen zusammengelegt. Jeder so entstehende
 * Abschnitt kennt seinen Horizont UND sein Substrat; ein Klick zeigt beides.
 *
 * Reine Funktionen ohne Angular, damit sie ohne TestBed pruefbar sind.
 */

export interface Abschnitt {
	/** Obergrenze in cm unter Gelaendeoberflaeche. */
	von: number;
	/** Untergrenze in cm. */
	bis: number;
	horizont: HorizontZeile | null;
	substrat: SubstratZeile | null;
}

/** Eine Zeile der Humusauflage; sie liegt UEBER der Gelaendeoberflaeche. */
export interface AuflageStreifen {
	feld: 'Ol' | 'Of' | 'Oh';
	maechtigkeit: number;
	/** Abstand der Unterkante ueber der Gelaendeoberflaeche in cm. */
	unten: number;
	/** Abstand der Oberkante ueber der Gelaendeoberflaeche in cm. */
	oben: number;
}

/**
 * Legt beide Folgen zu Abschnitten zusammen.
 *
 * Eine Zeile gilt BIS zu ihrer Tiefe (`tiefe` ist die Untergrenze). Gesucht
 * wird deshalb die erste Zeile, deren Untergrenze mindestens bis zur Mitte des
 * Abschnitts reicht.
 */
export function abschnitte(
	horizonte: HorizontZeile[] | undefined | null,
	substrate: SubstratZeile[] | undefined | null,
): Abschnitt[] {
	const grenzen = new Set<number>([0]);
	const sammle = (zeilen: { tiefe?: number }[] | undefined | null) => {
		for (const z of zeilen ?? []) {
			if (typeof z.tiefe === 'number' && Number.isFinite(z.tiefe) && z.tiefe > 0) {
				grenzen.add(z.tiefe);
			}
		}
	};
	sammle(horizonte);
	sammle(substrate);

	const sortiert = [...grenzen].sort((a, b) => a - b);
	const liste: Abschnitt[] = [];
	for (let i = 0; i < sortiert.length - 1; i++) {
		const von = sortiert[i];
		const bis = sortiert[i + 1];
		const mitte = (von + bis) / 2;
		liste.push({
			von,
			bis,
			horizont: (horizonte ?? []).find(z => (z.tiefe ?? 0) >= mitte) ?? null,
			substrat: (substrate ?? []).find(z => (z.tiefe ?? 0) >= mitte) ?? null,
		});
	}
	return liste;
}

/**
 * Die Zeilen der Humusauflage von unten nach oben (Oh, Of, Ol).
 *
 * Ol, Of und Oh stehen NICHT in der Horizontfolge, sondern als drei feste
 * Maechtigkeiten unter `extendedHeader.humusAuflage` (siehe humus-auflage.ts).
 * Sie liegen ueber der Gelaendeoberflaeche und werden im Bild auch dort
 * gezeichnet.
 *
 * `erfasst` sagt, ob in einem der drei Felder ueberhaupt eine Zahl steht -
 * auch eine 0. **Eine 0 ist erfasst**: Sie sagt „hier liegt keine Auflage",
 * und das ist etwas anderes als ein leeres Feld, bei dem niemand weisz, ob
 * nachgesehen wurde (Nutzer-Befund 20.09.2026). Gezeichnet wird in beiden
 * Faellen gleich; der Unterschied steht nur in der Beschriftung.
 */
export function auflageStreifen(auflage: HumusAuflage | undefined | null): {
	zeilen: AuflageStreifen[];
	gesamt: number;
	erfasst: boolean;
} {
	const reihe: { feld: AuflageStreifen['feld']; wert?: number }[] = [
		{ feld: 'Oh', wert: auflage?.oh },
		{ feld: 'Of', wert: auflage?.of },
		{ feld: 'Ol', wert: auflage?.ol },
	];
	const zeilen: AuflageStreifen[] = [];
	let hoehe = 0;
	let erfasst = false;
	for (const z of reihe) {
		// Eine Zahl zaehlt als Aussage, auch die 0. Was keine Zahl ist (leer,
		// NaN), sagt nichts.
		if (typeof z.wert === 'number' && Number.isFinite(z.wert)) erfasst = true;
		const m = typeof z.wert === 'number' && Number.isFinite(z.wert) && z.wert > 0 ? z.wert : 0;
		if (!m) continue;
		zeilen.push({ feld: z.feld, maechtigkeit: m, unten: hoehe, oben: hoehe + m });
		hoehe += m;
	}
	return { zeilen, gesamt: Math.round((hoehe + Number.EPSILON) * 100) / 100, erfasst };
}

/** Die tiefste erfasste Grenze - so tief reicht das Bild. */
export function gesamtTiefe(abs: Abschnitt[]): number {
	return abs.length ? abs[abs.length - 1].bis : 0;
}

/**
 * Traegt der Punkt ueberhaupt etwas, das sich zeichnen laesst?
 *
 * Ohne eine einzige Tiefe gibt es keinen Abschnitt und damit kein Bild; die
 * Humusauflage allein genuegt nicht, sie haette keinen Boden darunter.
 */
export function hatGrubenbild(bp: AufnahmePunkt | undefined | null): boolean {
	if (!bp) return false;
	return gesamtTiefe(abschnitte(bp.Horizontfolge, bp.Substratfolge)) > 0;
}
